import {
  Button,
  Flex,
  Grid,
  SelectField,
  TextAreaField,
  TextField,
} from "@aws-amplify/ui-react";
import { useEffect, useState } from "react";
import LocalizedStrings from "react-localization";

const FAKE_FARMS = [
  { id: "farm-001", name: "Finca Las Palmas" },
  { id: "farm-002", name: "Finca El Roble" },
  { id: "farm-003", name: "Finca La Esperanza" },
  { id: "farm-004", name: "Finca San José" },
  { id: "farm-005", name: "Finca Santa Rita" },
];

const FAKE_LOTS_BY_FARM = {
  "farm-001": [
    { id: "lot-001A", name: "Lote A" },
    { id: "lot-001B", name: "Lote B" },
    { id: "lot-001C", name: "Lote C" },
    { id: "lot-001D", name: "Lote D" },
    { id: "lot-001E", name: "Lote E" },
  ],
  "farm-002": [
    { id: "lot-002A", name: "Lote A" },
    { id: "lot-002B", name: "Lote B" },
    { id: "lot-002C", name: "Lote C" },
    { id: "lot-002D", name: "Lote D" },
    { id: "lot-002E", name: "Lote E" },
  ],
};

const FAKE_CROP_UNITS_BY_LOT = {
  "lot-001A": [
    { id: "cu-001", name: "Unidad 001" },
    { id: "cu-002", name: "Unidad 002" },
    { id: "cu-003", name: "Unidad 003" },
    { id: "cu-004", name: "Unidad 004" },
    { id: "cu-005", name: "Unidad 005" },
  ],
  "lot-002A": [
    { id: "cu-101", name: "Unidad 101" },
    { id: "cu-102", name: "Unidad 102" },
    { id: "cu-103", name: "Unidad 103" },
    { id: "cu-104", name: "Unidad 104" },
    { id: "cu-105", name: "Unidad 105" },
  ],
};

const FAKE_COLLABORATORS = [
  { id: "col-0001", name: "Juan Pérez" },
  { id: "col-0002", name: "María Gómez" },
  { id: "col-0003", name: "Carlos Rojas" },
  { id: "col-0004", name: "Ana Martínez" },
  { id: "col-0005", name: "Luis Díaz" },
];

const UNIT_OPTIONS = ["", "KG", "Ton"];

const SaleForm = ({
  onSubmit,
  type = "create",
  initialValues = {},
  onCancel,
}) => {
  const defaultState = {
    date: "",
    farmId: "",
    farmName: "",
    lotId: "",
    lotName: "",
    cropUnitId: "",
    cropUnitName: "",
    performedById: "",
    performedByName: "",
    saleClassification: { name: "", description: "" },
    unit: "KG",
    quantity: "",
    unitPrice: "",
    totalPrice: "",
    totalSale: "",
    photoEvidence: "",
    buyerData: { name: "", taxId: "", invoice: "" },
    additionalObservations: "",
    createdAt: "",
  };

  const [form, setForm] = useState({ ...defaultState, ...initialValues });
  const [availableLots, setAvailableLots] = useState([]);
  const [availableCropUnits, setAvailableCropUnits] = useState([]);

  useEffect(() => {
    setForm({ ...defaultState, ...initialValues });

    if (initialValues.farmId) {
      const lots = FAKE_LOTS_BY_FARM[initialValues.farmId] || [];
      setAvailableLots(lots);
      if (initialValues.lotId) {
        const cus = FAKE_CROP_UNITS_BY_LOT[initialValues.lotId] || [];
        setAvailableCropUnits(cus);
      }
    }

    if (initialValues.performedById && !initialValues.performedByName) {
      const found = FAKE_COLLABORATORS.find(
        (c) => c.id === initialValues.performedById
      );
      if (found) setForm((f) => ({ ...f, performedByName: found.name }));
    }

    // compute totals if quantity & unitPrice provided
    if (initialValues.quantity && initialValues.unitPrice) {
      const q = Number(initialValues.quantity) || 0;
      const up = Number(initialValues.unitPrice) || 0;
      const tot = Number((q * up).toFixed(2));
      setForm((f) => ({ ...f, totalPrice: tot, totalSale: tot }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const handleFarmChange = (farmId) => {
    const farm = FAKE_FARMS.find((f) => f.id === farmId);
    const lots = FAKE_LOTS_BY_FARM[farmId] || [];
    setAvailableLots(lots);
    setAvailableCropUnits([]);
    setForm({
      ...form,
      farmId,
      farmName: farm ? farm.name : "",
      lotId: "",
      lotName: "",
      cropUnitId: "",
      cropUnitName: "",
    });
  };

  const handleLotChange = (lotId) => {
    const lot = (availableLots || []).find((l) => l.id === lotId);
    const cus = FAKE_CROP_UNITS_BY_LOT[lotId] || [];
    setAvailableCropUnits(cus);
    setForm({
      ...form,
      lotId,
      lotName: lot ? lot.name : "",
      cropUnitId: "",
      cropUnitName: "",
    });
  };

  const handleCropUnitChange = (cropUnitId) => {
    const cu = (availableCropUnits || []).find((c) => c.id === cropUnitId);
    setForm({ ...form, cropUnitId, cropUnitName: cu ? cu.name : "" });
  };

  const handlePerformedByChange = (id) => {
    const found = FAKE_COLLABORATORS.find((c) => c.id === id);
    setForm({
      ...form,
      performedById: id,
      performedByName: found ? found.name : "",
    });
  };

  const handleQuantityOrPriceChange = (field, value) => {
    const next = { ...form, [field]: value };
    const q = Number(next.quantity) || 0;
    const up = Number(next.unitPrice) || 0;
    const total = Number((q * up).toFixed(2));
    next.totalPrice = total;
    next.totalSale = total;
    setForm(next);
  };

  const onSubmitForm = () => {
    const payload = { ...form };
    if (!payload.createdAt) payload.createdAt = new Date().toISOString();
    onSubmit && onSubmit(payload);
  };

  return (
    <Grid rowGap="12px" columnGap="12px" padding="20px">
      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <TextField
          name="date"
          label={strings.fields.date.label}
          placeholder="YYYY-MM-DD"
          value={form.date || ""}
          onChange={(e) => setForm({ ...form, date: e.target.value })}
        />
        <SelectField
          name="farmId"
          label={strings.fields.farmId.label}
          value={form.farmId || ""}
          onChange={(e) => handleFarmChange(e.target.value)}
        >
          <option value="">--</option>
          {FAKE_FARMS.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </SelectField>
      </Grid>

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <SelectField
          name="lotId"
          label={strings.fields.lotId.label}
          value={form.lotId || ""}
          onChange={(e) => handleLotChange(e.target.value)}
        >
          <option value="">--</option>
          {availableLots.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </SelectField>

        <SelectField
          name="cropUnitId"
          label={strings.fields.cropUnitId.label}
          value={form.cropUnitId || ""}
          onChange={(e) => handleCropUnitChange(e.target.value)}
        >
          <option value="">--</option>
          {availableCropUnits.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </SelectField>
      </Grid>

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <SelectField
          name="performedById"
          label={strings.fields.performedById.label}
          value={form.performedById || ""}
          onChange={(e) => handlePerformedByChange(e.target.value)}
        >
          <option value="">--</option>
          {FAKE_COLLABORATORS.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </SelectField>

        <TextField
          name="saleClassificationName"
          label={strings.fields.saleClassificationName.label}
          value={form.saleClassification?.name || ""}
          onChange={(e) =>
            setForm({
              ...form,
              saleClassification: {
                ...form.saleClassification,
                name: e.target.value,
              },
            })
          }
        />
      </Grid>

      <TextAreaField
        name="saleClassificationDescription"
        label={strings.fields.saleClassificationDescription.label}
        value={form.saleClassification?.description || ""}
        onChange={(e) =>
          setForm({
            ...form,
            saleClassification: {
              ...form.saleClassification,
              description: e.target.value,
            },
          })
        }
      />

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <SelectField
          name="unit"
          label={strings.fields.unit.label}
          value={form.unit || ""}
          onChange={(e) => setForm({ ...form, unit: e.target.value })}
        >
          {UNIT_OPTIONS.map((u) => (
            <option key={u} value={u}>
              {u || "--"}
            </option>
          ))}
        </SelectField>

        <TextField
          name="quantity"
          label={strings.fields.quantity.label}
          value={form.quantity || ""}
          onChange={(e) =>
            handleQuantityOrPriceChange("quantity", e.target.value)
          }
          placeholder="e.g. 1000"
        />
      </Grid>

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <TextField
          name="unitPrice"
          label={strings.fields.unitPrice.label}
          value={form.unitPrice || ""}
          onChange={(e) =>
            handleQuantityOrPriceChange("unitPrice", e.target.value)
          }
          placeholder="e.g. 1.50"
        />
        <TextField
          name="totalPrice"
          label={strings.fields.totalPrice.label}
          value={form.totalPrice !== undefined ? String(form.totalPrice) : ""}
          isReadOnly
        />
      </Grid>

      <TextAreaField
        name="additionalObservations"
        label={strings.fields.additionalObservations.label}
        value={form.additionalObservations || ""}
        onChange={(e) =>
          setForm({ ...form, additionalObservations: e.target.value })
        }
      />

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <TextField
          name="buyerName"
          label={strings.fields.buyerName.label}
          value={form.buyerData?.name || ""}
          onChange={(e) =>
            setForm({
              ...form,
              buyerData: { ...form.buyerData, name: e.target.value },
            })
          }
        />
        <TextField
          name="buyerTaxId"
          label={strings.fields.buyerTaxId.label}
          value={form.buyerData?.taxId || ""}
          onChange={(e) =>
            setForm({
              ...form,
              buyerData: { ...form.buyerData, taxId: e.target.value },
            })
          }
        />
      </Grid>

      <TextField
        name="buyerInvoice"
        label={strings.fields.buyerInvoice.label}
        value={form.buyerData?.invoice || ""}
        onChange={(e) =>
          setForm({
            ...form,
            buyerData: { ...form.buyerData, invoice: e.target.value },
          })
        }
        placeholder="factura.pdf"
      />

      <TextField
        name="photoEvidence"
        label={strings.fields.photoEvidence.label}
        placeholder="foto1.jpg, foto2.jpg"
        value={
          Array.isArray(form.photoEvidence)
            ? form.photoEvidence.join(", ")
            : form.photoEvidence || ""
        }
        onChange={(e) => setForm({ ...form, photoEvidence: e.target.value })}
      />

      <Flex justifyContent="end">
        <Button
          children={strings.fields.buttons.reset}
          onClick={() => setForm(defaultState)}
        />
        <Button
          children={
            type === "create"
              ? strings.fields.buttons.create
              : strings.fields.buttons.save
          }
          onClick={onSubmitForm}
          variation="primary"
        />
      </Flex>
    </Grid>
  );
};

const strings = new LocalizedStrings({
  es: {
    fields: {
      date: { label: "Fecha" },
      farmId: { label: "Finca" },
      lotId: { label: "Lote" },
      cropUnitId: { label: "Unidad de cultivo" },
      performedById: { label: "Persona que realiza" },
      saleClassificationName: { label: "Clasificación (Nombre)" },
      saleClassificationDescription: { label: "Clasificación (Descripción)" },
      unit: { label: "Unidad" },
      quantity: { label: "Cantidad" },
      unitPrice: { label: "Valor unitario" },
      totalPrice: { label: "Valor total" },
      photoEvidence: { label: "Evidencia fotográfica" },
      buyerName: { label: "Nombre comprador" },
      buyerTaxId: { label: "NIT comprador" },
      buyerInvoice: { label: "Factura comprador (archivo)" },
      additionalObservations: { label: "Observaciones adicionales" },
      createdAt: { label: "Fecha de creación" },
      buttons: {
        create: "Crear",
        save: "Guardar",
        reset: "Restablecer",
        cancel: "Cancelar",
      },
    },
    modal: {},
  },
});

export default SaleForm;
